import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { SubscriptionLike } from 'rxjs';

import { NotificationService, SearchHistoriesService, SessionStorageService, ToasterService } from '@shared/services';
import { SearchTermHistoryEntryTypeEnum, SearchTermHistoryModel } from '@shared/models';

@Component({
  selector:     'pagination-search',
  templateUrl:  'pagination-search.component.html',
  host: { class: 'am-flex-align relative' },
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class PaginationSearchComponent implements OnInit, OnDestroy {
  @Input() tooltipItems: string[];
  @Input() entryType:    SearchTermHistoryEntryTypeEnum;
  @Input() secondary:    string;
  @Input() loading:      boolean;
  @Output() callback = new EventEmitter<string>();

  subscriptions:   SubscriptionLike[]       = [];
  searchValue:     string;
  suggestions:     SearchTermHistoryModel[] = [];
  showSuggestions: boolean = false;
  timeout:         any;
  constructor(
    private notificationService:    NotificationService,
    private sessionStorageService:  SessionStorageService,
    public  toasterService:         ToasterService,
    private searchHistoriesService: SearchHistoriesService,
    @Inject(DOCUMENT) public readonly document: Document
  ) { }

  ngOnInit(): void {
    if (!this.secondary) this.subscriptions.push(this.sessionStorageService.searchValue.subscribe(query => this.searchValue = query));
    else                 this.subscriptions.push(this.sessionStorageService.searchSecondaryValue.subscribe(search => { if (!search || search.useCase === this.secondary) this.searchValue = search?.query; }));

    if (!this.secondary && this.entryType) {
      this.searchHistoriesService.init(this.entryType);
      this.subscriptions.push(this.searchHistoriesService.searchTermHistoriesObserver.subscribe((terms) => {
        this.suggestions = terms.slice(0, 5);
      }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.subscriptions = [];
  }

  onSearchValueChange(query: string): void {
    if (!this.secondary) this.showSuggestions = true;
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.updateWithSearch(query);
    }, 1000);
  }

  clearSearch(): void {
    this.searchValue = '';
    this.updateWithSearch(this.searchValue);
    this.clearSuggestions();
  }

  onSearchValueFocusIn(query: string): void {
    if (!this.secondary) {
      this.searchHistoriesService.searchTermsStartingWith(query);
      this.showSuggestions = true;
    }
  }

  onSearchValueFocusOut(): void {
    if (!this.secondary) {
      setTimeout(() => this.showSuggestions = false, 500);
    }
  }

  onSearchValueKeydown(event: any): void {
    if (!this.secondary) {
      this.showSuggestions = true;
      event.stopPropagation();
      if (event.key === 'Enter') {
        this.clearSuggestions();
      }
    }
  }

  pickSuggestion(term: SearchTermHistoryModel) {
    this.searchHistoriesService.hitSearchHistory(term);
    this.searchValue = term.search_term;

    this.clearSuggestions();
    this.updateWithSearch(this.searchValue);
  }

  deleteSuggestion(event: any, term: SearchTermHistoryModel): void {
    event.stopPropagation();
    this.searchHistoriesService.deleteSearchHistory(term);
  }

  private updateWithSearch(query: string): void {
    if (!this.secondary) {
      this.notificationService.wait();
      this.searchHistoriesService.searchTermsStartingWith(query);
      this.sessionStorageService.changeSearchValue(query.trim());
      this.sessionStorageService.changePageNumber(1);
      const suggestion = this.suggestions.find((term) => term.search_term === query);
      if (suggestion) this.searchHistoriesService.hitSearchHistory(suggestion);
    } else this.sessionStorageService.changeSearchSecondaryValue({ useCase: this.secondary, query: query.trim() });
    this.callback.emit();
  }

  private clearSuggestions() {
    this.showSuggestions = false;
    this.suggestions = [];
  }

}
